import React, { useState } from 'react';
import { Container } from 'styles';
import { useQuery } from '@apollo/client';
import { trimWhiteSpace } from '@perch-labs/warbler';
import { Text } from '@perch-labs/shrike';
import { copyFor } from 'config';
import { useAccountContext } from 'contexts/account';
import { LoadingModule } from 'components/loading';
import { ErrorContent } from 'components/errors';
import { CarouselButtons } from 'components/carousel-buttons';
import { StandardRow } from 'components/standard-row';
import {
  SEARCH_UTILITY_ACCOUNTS,
  SEARCH_UTILITY_ACCOUNTS_PAGINATION_LIMIT,
} from '../utility-sync-credential.api';

const getCopy = copyFor('settings.utilitySyncCredential');

interface Props {
  utilityCredentialId: number;
}

export const PaginatedUtilityAccounts = ({ utilityCredentialId }: Props) => {
  const { selectedAccountId } = useAccountContext();
  const [page, setPage] = useState(0);
  const { data, loading, error } = useQuery<
    SearchUtilityAccountsQuery,
    SearchUtilityAccountsQueryVariables
  >(SEARCH_UTILITY_ACCOUNTS, {
    variables: {
      accountId: selectedAccountId,
      utilityCredentialId,
      page: page + 1,
    },
  });

  if (loading) {
    return (
      <LoadingModule
        variant="list"
        padding={{ top: '16px', bottom: '16px' }}
        bufferProps={{ width: '0' }}
        rowCount="3"
      />
    );
  }
  if (error || !data) {
    return <ErrorContent />;
  }

  const { utilityAccounts, count } = data.search.utilityAccounts;
  const showPagination = count > SEARCH_UTILITY_ACCOUNTS_PAGINATION_LIMIT;

  const utilityAccountRow = ua => {
    const accountNumber = ua?.accountNumber;
    const utilityAccountNumber = accountNumber
      ? ` #${trimWhiteSpace(accountNumber)}`
      : '';

    const serviceAddress = ua?.displayServiceAddress;

    const content = (
      <div>
        <Text tag="h3" textStyle="heading500">
          {serviceAddress || getCopy('utilityAccount.noServiceAddress')}
        </Text>
        <Text color="secondary" margin={{ top: '8px' }}>
          {utilityAccountNumber
            ? getCopy('utilityAccounts.accountNumberLabel', {
                utilityAccountNumber,
              })
            : getCopy('utilityAccount.noAccountNumber')}
        </Text>
      </div>
    );

    return (
      <StandardRow
        key={`utility-account-display-${ua?.id}`}
        hasBottomBorder={showPagination}
      >
        {content}
      </StandardRow>
    );
  };

  return (
    <Container>
      {utilityAccounts.map(ua => utilityAccountRow(ua))}
      {showPagination && (
        <CarouselButtons
          index={page}
          setIndex={setPage}
          itemName="page"
          length={Math.ceil(count / SEARCH_UTILITY_ACCOUNTS_PAGINATION_LIMIT)}
        />
      )}
    </Container>
  );
};
